/**
 * Shared authentication types and JWT decode utility.
 *
 * Provides lightweight client-side JWT payload decoding (base64, no
 * signature verification — the server handles that) and the canonical
 * type definitions used by the {@link AuthProvider} and route guards
 * across all micro-frontends.
 *
 * @module lib/auth
 */

import { tryCatch } from '@learnai/tools/try-catch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * User object reconstructed from JWT claims **or** the server-validated
 * `/auth/session` response.
 *
 * `birthday` is **only** populated by the server response (the JWT does
 * not carry it). Consumers gating on profile completeness should rely on
 * `AuthState.profileComplete` rather than reading `birthday` directly so
 * the optimistic JWT-only render does not flash an "incomplete" state.
 */
export interface AuthUser {
	id: string;
	email: string;
	name: string | null;
	avatarUrl: string | null;
	emailVerified: string | null;
	/** ISO-8601 calendar date (YYYY-MM-DD); only set after server validation. */
	birthday?: string | null;
}

/**
 * Zone-level access scope derived from the user's org memberships and
 * class enrollments.
 *
 * - `admin`   — user has at least one org membership with `OWNER`, `ADMIN`,
 *               or `MEMBER` role, or is an `INSTRUCTOR` / `ASSISTANT` in
 *               any class.
 * - `student` — user has a `STUDENT` org membership, or at least one
 *               `STUDENT` or `AUDITOR` class enrollment.
 */
export type AuthScope = 'admin' | 'student';

/** Full authentication state exposed via React context. */
export interface AuthState {
	/** The authenticated user, or `null` when logged out / loading. */
	user: AuthUser | null;
	/** Resolved zone-level scopes for the current user. */
	scopes: AuthScope[];
	/** `true` once the initial auth check has completed. */
	isReady: boolean;
	/** `true` while the initial auth check is in-flight. */
	isLoading: boolean;
	/** Convenience flag — equivalent to `user !== null`. */
	isAuthenticated: boolean;
	/**
	 * `true` once the server-validated user has both a non-empty `name`
	 * and a non-null `birthday`. `false` while loading or when the JWT
	 * has been decoded but the server response has not yet arrived, so
	 * gating UI should also check `isReady` to avoid flashing the
	 * "incomplete" state.
	 */
	profileComplete: boolean;
	/** Returns `true` if the user has the given scope. */
	hasScope: (scope: AuthScope) => boolean;
	/** Sign out — revokes the refresh token and clears stored tokens. */
	logout: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// JWT payload (mirrors server's JwtPayload — kept in sync manually)
// ---------------------------------------------------------------------------

/** Raw claims embedded in the HS256 access token. */
interface JwtClaims {
	sub: string;
	email: string;
	name: string | null;
	avatarUrl: string | null;
	emailVerified: string | null;
	iat?: number;
	exp?: number;
}

// ---------------------------------------------------------------------------
// Org / Class role literals (duplicated from server enums to avoid a
// direct dependency on @learnai/database in frontend packages)
// ---------------------------------------------------------------------------

/** Organization-level roles. */
export type OrgRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'STUDENT';

/** Class-level roles. */
export type ClassRole = 'INSTRUCTOR' | 'ASSISTANT' | 'STUDENT' | 'AUDITOR';

/** Org roles that grant access to the admin zone. */
const ADMIN_ORG_ROLES: ReadonlySet<OrgRole> = new Set(['OWNER', 'ADMIN', 'MEMBER']);

/** Class roles that grant access to the admin zone. */
const ADMIN_CLASS_ROLES: ReadonlySet<ClassRole> = new Set(['INSTRUCTOR', 'ASSISTANT']);

/** Class roles that grant access to the student zone. */
const STUDENT_CLASS_ROLES: ReadonlySet<ClassRole> = new Set(['STUDENT', 'AUDITOR']);

// ---------------------------------------------------------------------------
// JWT decode (no verification — the server verifies signatures)
// ---------------------------------------------------------------------------

/**
 * Decodes the payload section of a JWT access token.
 *
 * This is a **client-side convenience only** — it performs no signature
 * verification. The server validates every request independently.
 *
 * Returns `null` if the token is malformed or missing required claims.
 */
export function decodeAccessToken(token: string): AuthUser | null {
	const { data: claims, error } = tryCatch(() => {
		const parts = token.split('.');
		if (parts.length !== 3) return null;

		// Base64url → Base64 → binary → JSON
		const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
		const json = atob(base64);
		return JSON.parse(json) as JwtClaims;
	});

	if (error || !claims) return null;
	if (typeof claims.sub !== 'string' || typeof claims.email !== 'string') return null;

	// Check expiry — reject tokens that have already expired.
	if (claims.exp) {
		const expiry = Temporal.Instant.fromEpochMilliseconds(claims.exp * 1000);
		if (Temporal.Instant.compare(expiry, Temporal.Now.instant()) < 0) return null;
	}

	return {
		id: claims.sub,
		email: claims.email,
		name: claims.name ?? null,
		avatarUrl: claims.avatarUrl ?? null,
		emailVerified: claims.emailVerified ?? null,
	};
}

// ---------------------------------------------------------------------------
// Scope resolution
// ---------------------------------------------------------------------------

/** Minimal membership shape returned by `GET /users/me/memberships`. */
export interface MembershipEntry {
	id: string;
	role: OrgRole;
	organizationId: string;
}

/** Minimal enrollment shape returned by `GET /users/me/enrollments`. */
export interface EnrollmentEntry {
	id: string;
	role: ClassRole;
	classId: string;
}

/**
 * Derives the set of {@link AuthScope}s a user should have based on
 * their org memberships and class enrollments.
 *
 * Rules:
 * - OWNER / ADMIN / MEMBER org membership → `admin`
 * - STUDENT org membership → `student`
 * - INSTRUCTOR or ASSISTANT enrollment → `admin`
 * - STUDENT or AUDITOR enrollment → `student`
 */
export function resolveScopes(memberships: MembershipEntry[], enrollments: EnrollmentEntry[]): AuthScope[] {
	const scopes = new Set<AuthScope>();

	for (const membership of memberships) {
		if (ADMIN_ORG_ROLES.has(membership.role)) {
			scopes.add('admin');
		} else if (membership.role === 'STUDENT') {
			scopes.add('student');
		}
	}

	for (const enrollment of enrollments) {
		if (ADMIN_CLASS_ROLES.has(enrollment.role)) {
			scopes.add('admin');
		}
		if (STUDENT_CLASS_ROLES.has(enrollment.role)) {
			scopes.add('student');
		}
	}

	return Array.from(scopes);
}

// ---------------------------------------------------------------------------
// Redirect helpers
// ---------------------------------------------------------------------------

/**
 * Returns the best landing zone URL for a user given their scopes.
 *
 * Priority: admin > student > root.
 */
export function defaultRedirectForScopes(scopes: AuthScope[]): string {
	if (scopes.includes('admin')) return '/admin';
	if (scopes.includes('student')) return '/learn';
	return '/onboard';
}
